<template>
    <Teleport to="body">
        <div class="modal-backdrop" @click.self="$emit('close')">
            <div class="modal-panel" role="dialog" aria-modal="true" :aria-label="`Route details: ${entryName} to ${exitName}`" ref="panel" tabindex="-1">
                <header class="modal-head">
                    <div>
                        <h2 class="modal-title">
                            {{ entryName }} <svg class="ico" aria-hidden="true"><use href="#i-arrow-right-long"></use></svg> {{ exitName }}
                        </h2>
                        <p class="modal-sub" v-if="data">
                            {{ data.expressway.name }} ({{ data.expressway.code }})
                        </p>
                    </div>
                    <button type="button" class="modal-close" @click="$emit('close')" aria-label="Close">
                        <svg class="ico" aria-hidden="true"><use href="#i-xmark"></use></svg>
                    </button>
                </header>

                <div class="modal-body">
                    <p v-if="loading" class="calc-loading">Loading route details…</p>
                    <p v-else-if="loadError" class="calc-error">Could not load route details. Please try again.</p>

                    <template v-else-if="data">
                        <!-- Summary strip -->
                        <div class="modal-summary">
                            <div class="ms-item">
                                <span class="ms-label">Toll fee</span>
                                <span class="ms-value ms-fee">₱{{ fmt(selectedFare) }}</span>
                                <span class="ms-note">Class {{ vehicleClass }} · {{ roundTrip ? 'round trip' : 'one way' }}</span>
                            </div>
                            <div class="ms-item">
                                <span class="ms-label">Stops on the way</span>
                                <span class="ms-value">{{ data.interchanges }}</span>
                                <span class="ms-note">interchange{{ data.interchanges === 1 ? '' : 's' }} passed</span>
                            </div>
                            <div class="ms-item">
                                <span class="ms-label">RFID</span>
                                <span class="ms-value ms-rfid">{{ data.expressway.rfid }}</span>
                                <span class="ms-note">{{ data.expressway.system_type }} system</span>
                            </div>
                        </div>

                        <!-- Journey timeline -->
                        <h3 class="modal-h3"><svg class="ico" aria-hidden="true"><use href="#i-route"></use></svg> Your journey, stop by stop</h3>
                        <p class="modal-hint">
                            Fares are from <strong>{{ entryName }}</strong> to each exit — useful if you decide to get off early.
                            Places listed are within about 2.5&nbsp;km of each interchange.
                        </p>
                        <ol class="timeline">
                            <li v-for="stop in data.stops" :key="stop.id"
                                :class="{ 'tl-entry': stop.is_entry, 'tl-exit': stop.is_exit }">
                                <span class="tl-dot" aria-hidden="true">
                                    <svg class="ico" v-if="stop.is_entry" aria-hidden="true"><use href="#i-right-to-bracket"></use></svg>
                                    <svg class="ico" v-else-if="stop.is_exit" aria-hidden="true"><use href="#i-flag-checkered"></use></svg>
                                </span>
                                <div class="tl-content">
                                    <div class="tl-row">
                                        <span class="tl-name">
                                            {{ stop.name }}
                                            <small v-if="stop.is_entry">Entry point</small>
                                            <small v-else-if="stop.is_exit">Your exit</small>
                                        </span>
                                        <span v-if="stop.fares" class="tl-fare" :class="{ 'tl-fare-final': stop.is_exit }">
                                            ₱{{ fmt(tripValue(stop.fares[String(vehicleClass)])) }}
                                        </span>
                                        <span v-else class="tl-fare tl-fare-none">—</span>
                                    </div>
                                    <p v-if="stop.notes" class="tl-notes">{{ stop.notes }}</p>
                                    <ul v-if="stop.amenities && stop.amenities.length" class="tl-amenity-list">
                                        <li v-for="group in stop.amenities" :key="group.type">
                                            <svg class="ico tl-amenity-icon" aria-hidden="true"><use :href="'#i-' + String(group.icon).slice(3)"></use></svg>
                                            <span>
                                                <strong>{{ group.label }}:</strong>
                                                {{ group.items.map(i => i.name).join(', ') }}<template v-if="group.total > group.items.length"> +{{ group.total - group.items.length }} more</template>
                                                <em class="tl-amenity-dist" v-if="group.items[0].distance_m">
                                                    · nearest {{ formatDistance(group.items[0].distance_m) }}
                                                </em>
                                            </span>
                                        </li>
                                    </ul>
                                    <p v-if="stop.connections.length" class="tl-connections">
                                        <svg class="ico" aria-hidden="true"><use href="#i-code-branch"></use></svg>
                                        Connects to
                                        <span v-for="(c, i) in stop.connections" :key="c.code">
                                            <strong>{{ c.code }}</strong>{{ i < stop.connections.length - 1 ? ', ' : '' }}
                                        </span>
                                    </p>
                                    <a class="tl-directions"
                                       :href="mapsUrl(stop.name)"
                                       target="_blank" rel="noopener">
                                        <svg class="ico" aria-hidden="true"><use href="#i-diamond-turn-right"></use></svg> Directions
                                    </a>
                                </div>
                            </li>
                        </ol>

                        <!-- All classes -->
                        <h3 class="modal-h3"><svg class="ico" aria-hidden="true"><use href="#i-car-side"></use></svg> Fare by vehicle class</h3>
                        <table class="modal-table">
                            <tbody>
                                <tr v-for="c in classes" :key="c.value" :class="{ 'rate-row-selected': c.value === vehicleClass }">
                                    <td><svg class="ico" aria-hidden="true"><use :href="'#i-' + String(c.icon).slice(3)"></use></svg> Class {{ c.value }} — {{ c.desc }}</td>
                                    <td>₱{{ fmt(tripValue(finalFares[String(c.value)])) }}</td>
                                </tr>
                            </tbody>
                        </table>

                        <!-- Practical info -->
                        <h3 class="modal-h3"><svg class="ico" aria-hidden="true"><use href="#i-circle-info"></use></svg> Before you drive</h3>
                        <ul class="modal-facts">
                            <li>
                                <strong>Payment:</strong> {{ data.expressway.code }} uses
                                <strong>{{ data.expressway.rfid }} RFID</strong>. Load at least
                                <strong>₱{{ fmt(suggestedLoad) }}</strong> for a round trip with buffer.
                            </li>
                            <li v-if="data.expressway.system_type === 'closed'">
                                <strong>Closed system:</strong> your fare depends on where you enter and exit — tap in and tap out.
                            </li>
                            <li v-else-if="data.expressway.system_type === 'open'">
                                <strong>Open system:</strong> a flat fee is charged at the barrier regardless of how far you travel.
                            </li>
                            <li v-else>
                                <strong>Mixed system:</strong> part of this expressway charges a flat barrier fee, the rest is distance-based.
                            </li>
                            <li v-if="data.expressway.operator"><strong>Operator:</strong> {{ data.expressway.operator }}</li>
                            <li v-if="data.expressway.length_km"><strong>Full length:</strong> {{ data.expressway.length_km }} km end to end</li>
                            <li>
                                <strong>Rates:</strong> VAT-inclusive.
                                <template v-if="data.expressway.effective_date">Effective {{ data.expressway.effective_date }}.</template>
                                <template v-if="data.expressway.verified_at && data.expressway.source_url">
                                    ✓ Verified against
                                    <a :href="data.expressway.source_url" target="_blank" rel="noopener nofollow">{{ data.expressway.source_label }}</a>
                                    on {{ data.expressway.verified_at }}.
                                </template>
                                <template v-else>Pending verification against the official matrix.</template>
                                The fee at the plaza always prevails.
                            </li>
                        </ul>
                    </template>
                </div>

                <footer class="modal-foot" v-if="data">
                    <a class="btn btn-ghost" :href="data.route_url">Open full route page</a>
                    <a class="btn btn-ghost" :href="data.expressway.url">{{ data.expressway.code }} toll matrix</a>
                    <a class="btn" :href="mapsUrl(exitName)" target="_blank" rel="noopener">
                        <svg class="ico" aria-hidden="true"><use href="#i-diamond-turn-right"></use></svg> Directions to {{ exitName }}
                    </a>
                    <p class="modal-attribution">
                        Nearby places from
                        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener nofollow">OpenStreetMap</a> (ODbL).
                    </p>
                </footer>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

const props = defineProps({
    entryId: { type: Number, required: true },
    exitId: { type: Number, required: true },
    entryName: { type: String, required: true },
    exitName: { type: String, required: true },
    vehicleClass: { type: Number, default: 1 },
    roundTrip: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const data = ref(null);
const loading = ref(true);
const loadError = ref(false);
const panel = ref(null);

const classes = [
    { value: 1, icon: 'fa-car-side', desc: 'Cars, SUVs & vans' },
    { value: 2, icon: 'fa-bus', desc: 'Buses & light trucks' },
    { value: 3, icon: 'fa-truck-moving', desc: 'Heavy trucks & trailers' },
];

const finalFares = computed(() => {
    const last = data.value?.stops?.find((s) => s.is_exit);
    return last?.fares ?? {};
});

const selectedFare = computed(() => tripValue(finalFares.value[String(props.vehicleClass)]));

const suggestedLoad = computed(() => {
    const oneWay = finalFares.value[String(props.vehicleClass)];
    if (!oneWay) return 0;
    return Math.ceil((oneWay * 2 * 1.2) / 50) * 50;
});

function tripValue(v) {
    if (v === undefined || v === null) return null;
    return props.roundTrip ? v * 2 : v;
}

const fmt = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('en-PH').format(v));

function formatDistance(metres) {
    if (!metres) return '';
    return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${metres} m`;
}

function mapsUrl(pointName) {
    const dest = `${pointName} Exit, ${data.value?.expressway.name ?? ''}, Philippines`;
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;
}

function onKey(e) {
    if (e.key === 'Escape') emit('close');
}

onMounted(async () => {
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    panel.value?.focus();
    try {
        const params = new URLSearchParams({ entry: props.entryId, exit: props.exitId });
        const res = await fetch(`/api/calculator/route-details?${params}`);
        if (!res.ok) throw new Error();
        data.value = await res.json();
    } catch {
        loadError.value = true;
    } finally {
        loading.value = false;
    }
});

onUnmounted(() => {
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
});
</script>
